import { TargetCamera } from '@babylonjs/core/Cameras/targetCamera';
import { Engine } from '@babylonjs/core/Engines/engine';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import '@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent';
import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { CubeTexture } from '@babylonjs/core/Materials/Textures/cubeTexture';
import { CreateBox } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { CreateDisc } from '@babylonjs/core/Meshes/Builders/discBuilder';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Scene } from '@babylonjs/core/scene';
import { registerBuiltInLoaders } from '@babylonjs/loaders/dynamic';
import { ASSET_MANIFEST } from '../../assets/manifest/assetManifest';
import type { HammerConfig } from '../../config/minigames.config';
import { HAMMER_IMPACT_AT, type HammerGame } from '../../minigames/HammerGame';
import { AttemptGrade } from '../../minigames/MiniGame';
import { easeIn, easeOut } from './paint';

/** The set's GLB from the asset manifest (PROP_MG_HAMMER_SET, loaded on first use, not before the run). */
const modelUrl = (): string => {
  const entry = ASSET_MANIFEST.find((e) => e.id === 'PROP_MG_HAMMER_SET');
  if (entry?.source.kind !== 'glb') throw new Error('PROP_MG_HAMMER_SET is missing in the asset manifest');
  return entry.source.url;
};
/** Model sizes (m) – tools/blender/assets/MG_HammerSet.py. */
const MODEL = { faceRadius: 0.0165, nailHeadRadius: 0.0075, nailLength: 0.09, handleHand: 0.3, handleHeight: 0.044 } as const;
/** How far the nail stands out of the beam before the first strike (m). */
const PROTRUDE = 0.052;
/** Face over the nail head while the hammer swings (m). */
const LIFT = 0.075;
/** Handle towards the viewer's right (rad around the vertical). */
const HANDLE_YAW = -0.62;

/**
 * Hammer & nail in 3D (user 2026-10-04: GLB instead of drawings): claw hammer, nail and spruce beam from the Blender
 * set, a light almost straight from above – the hammer's shadow on the wood shows where it will come down – and the
 * game's sky for the steel's reflections. Its own small engine on the mini-game canvas, made once and kept.
 */
export class HammerStageView {
  private engine: Engine | null = null;
  private scene: Scene | null = null;
  private camera: TargetCamera | null = null;
  private wrist: TransformNode | null = null;
  private face: TransformNode | null = null;
  private nail: AbstractMesh | null = null;
  private readonly dents: AbstractMesh[] = [];
  private loading: Promise<void> | null = null;
  private ready = false;
  /** Swing amplitude (m): game.x 1 is this far from the nail – the face reaches the nail head as far as a hit counts. */
  private readonly amp: number;
  private readonly cameraHome = new Vector3(0.06, 0.36, 0.44);
  private readonly target = new Vector3(0.03, 0.045, 0);

  constructor(
    private readonly config: HammerConfig,
    private readonly envUrl: string,
  ) {
    this.amp = (MODEL.faceRadius + MODEL.nailHeadRadius) / config.hitHalfWidth;
  }

  get isReady(): boolean {
    return this.ready;
  }

  /** Creates the engine on the canvas and loads the set (once). */
  prepare(canvas: HTMLCanvasElement): Promise<void> {
    this.loading ??= this.build(canvas).then(() => {
      this.ready = true;
    });
    return this.loading;
  }

  /** Draws the game's state; returns the nail head on screen (CSS px) for the sparks, or null before it is loaded. */
  render(game: HammerGame, t: number): { x: number; y: number } | null {
    if (!this.ready || !this.engine || !this.scene || !this.camera || !this.wrist || !this.face || !this.nail) return null;
    this.engine.resize();
    const headY = (1 - game.depth) * PROTRUDE;
    this.nail.position.set(0, headY, 0);

    // dents where a strike missed
    const misses = game.strikes.filter((s) => s.x !== null && s.grade === AttemptGrade.Miss);
    this.dents.forEach((d, i) => {
      const s = misses[i];
      d.setEnabled(s !== undefined);
      if (s?.x != null) d.position.set(s.x * this.amp, 0.0006, 0);
    });

    // hammer: lift by the strike animation, tilted around the wrist (the head swings on an arc), face over game.x
    const lift = this.lift(game);
    this.wrist.rotation.set(0, HANDLE_YAW, -0.06 - (lift / LIFT) * 0.38);
    this.wrist.position.set(0, 0, 0);
    this.wrist.computeWorldMatrix(true);
    const face = this.face.getAbsolutePosition();
    this.wrist.position.set(game.x * this.amp - face.x, headY + lift - face.y, -face.z);

    // a jolt on impact
    const age = game.secondsSinceGrade;
    const jolt = age < 0.3 ? Math.exp(-age * 16) * (game.grade === AttemptGrade.Perfect ? 0.006 : 0.003) : 0;
    this.camera.position.set(this.cameraHome.x + Math.sin(t * 90) * jolt, this.cameraHome.y + Math.cos(t * 77) * jolt, this.cameraHome.z);
    this.camera.setTarget(this.target);
    this.scene.render();

    const p = Vector3.Project(new Vector3(0, headY, 0), Matrix.IdentityReadOnly, this.scene.getTransformMatrix(), this.camera.viewport.toGlobal(this.engine.getRenderWidth(), this.engine.getRenderHeight()));
    const scale = this.engine.getHardwareScalingLevel();
    return { x: p.x * scale, y: p.y * scale };
  }

  dispose(): void {
    this.scene?.dispose();
    this.engine?.dispose();
    this.engine = null;
    this.scene = null;
    this.ready = false;
    this.loading = null;
  }

  private lift(game: HammerGame): number {
    const s = game.strike;
    if (s === null) return LIFT;
    if (s < HAMMER_IMPACT_AT) return LIFT * (1 - easeIn(s / HAMMER_IMPACT_AT));
    const k = (s - HAMMER_IMPACT_AT) / (1 - HAMMER_IMPACT_AT);
    return k < 0.25 ? 0 : LIFT * easeOut((k - 0.25) / 0.75);
  }

  private async build(canvas: HTMLCanvasElement): Promise<void> {
    registerBuiltInLoaders();
    const engine = new Engine(canvas, true, { stencil: false, preserveDrawingBuffer: false, powerPreference: 'high-performance' }, true);
    engine.setHardwareScalingLevel(1 / Math.min(2, window.devicePixelRatio || 1));
    const scene = new Scene(engine);
    // right-handed like glTF: the loader mirrors nothing, the nodes can be steered directly (camera on +Z, X to the right)
    scene.useRightHandedSystem = true;
    scene.clearColor = new Color4(0.07, 0.08, 0.1, 1);
    scene.environmentTexture = CubeTexture.CreateFromPrefilteredData(this.envUrl, scene);
    scene.environmentIntensity = 0.75;
    const camera = new TargetCamera('MG_CAMERA', this.cameraHome.clone(), scene);
    camera.fov = 0.72;
    camera.minZ = 0.01;
    camera.maxZ = 10;
    camera.setTarget(this.target);

    const sky = new HemisphericLight('MG_SKY', new Vector3(0.2, 1, -0.3), scene);
    sky.intensity = 0.55;
    sky.groundColor = new Color3(0.25, 0.2, 0.16);
    const sun = new DirectionalLight('MG_SUN', new Vector3(-0.12, -1, 0.18), scene);
    sun.position = new Vector3(0.1, 1, -0.2);
    sun.intensity = 2.0;
    const shadows = new ShadowGenerator(1024, sun);
    // PCF: crisp enough to aim with, robust at this tiny scale (metres of a hammer)
    shadows.usePercentageCloserFiltering = true;
    shadows.filteringQuality = ShadowGenerator.QUALITY_HIGH;
    shadows.bias = 0.0005;
    shadows.darkness = 0.25;
    sun.autoCalcShadowZBounds = true;

    // the floor and the wall behind (concrete), so the beam does not float in the dark
    const concrete = new StandardMaterial('MG_CONCRETE', scene);
    concrete.diffuseColor = new Color3(0.13, 0.135, 0.145);
    concrete.specularColor = new Color3(0.05, 0.05, 0.05);
    const floor = CreateBox('MG_FLOOR', { width: 3, height: 0.02, depth: 3 }, scene);
    floor.position.y = -0.13;
    floor.material = concrete;
    floor.receiveShadows = true;
    const wall = CreateBox('MG_WALL', { width: 3, height: 1.5, depth: 0.02 }, scene);
    wall.position.set(0, 0.5, -0.32);
    const wallMaterial = new StandardMaterial('MG_WALL', scene);
    wallMaterial.diffuseColor = new Color3(0.1, 0.11, 0.13);
    wallMaterial.specularColor = Color3.Black();
    wall.material = wallMaterial;

    const container = await LoadAssetContainerAsync(modelUrl(), scene);
    container.addAllToScene();
    const node = (name: string) => container.meshes.find((m) => m.name === name) ?? container.transformNodes.find((n) => n.name === name);
    const hammer = node('MG_HAMMER') as AbstractMesh;
    const nail = node('MG_NAIL') as AbstractMesh;
    const beam = node('MG_BEAM') as AbstractMesh;
    for (const mesh of container.meshes) {
      mesh.receiveShadows = true;
      if (mesh.getTotalVertices() > 0) shadows.addShadowCaster(mesh);
    }

    // wrist: the hammer hangs from the hand at the end of the handle and turns around it
    const wrist = new TransformNode('MG_WRIST', scene);
    const faceNode = new TransformNode('MG_FACE', scene);
    hammer.setParent(null);
    hammer.parent = wrist;
    hammer.rotationQuaternion = null;
    hammer.rotation.set(0, 0, 0);
    hammer.position.set(-MODEL.handleHand, -MODEL.handleHeight, 0);
    faceNode.parent = hammer;
    faceNode.position.set(0, 0, 0);

    nail.setParent(null);
    nail.rotationQuaternion = null;
    beam.setParent(null);
    beam.rotationQuaternion = null;
    beam.position.set(0, 0, 0);

    const dentMaterial = new StandardMaterial('MG_DENT', scene);
    dentMaterial.diffuseColor = new Color3(0.25, 0.15, 0.07);
    dentMaterial.alpha = 0.55;
    dentMaterial.specularColor = Color3.Black();
    dentMaterial.zOffset = -2;
    dentMaterial.backFaceCulling = false;
    for (let i = 0; i < this.config.strikes; i++) {
      const dent = CreateDisc(`MG_DENT_${i}`, { radius: MODEL.faceRadius * 0.95, tessellation: 20 }, scene);
      dent.rotation.x = Math.PI / 2;
      dent.scaling.y = 0.6;
      dent.material = dentMaterial;
      dent.setEnabled(false);
      this.dents.push(dent);
    }

    this.engine = engine;
    this.scene = scene;
    this.camera = camera;
    this.wrist = wrist;
    this.face = faceNode;
    this.nail = nail;
  }
}
